import { createRouter, createWebHistory, type RouteLocation } from "vue-router";
import { watch } from "vue";
import { bootstrapReady } from "@/bootstrapState";
import AppLayout from "./components/system/AppLayout.vue";
import LoginPage from "./components/login/LoginPage.vue";
import { canInAnyTenant } from "@/composables/tenantAbility";
import { isRouteFeatureEnabled } from "@/services/featureFlags";
import { installChunkLoadRecovery } from "@/services/chunkLoadRecovery";
import { UserPreferencesService } from "@/services/UserPreferencesService";

const routes = [
  {
    path: "/login",
    name: "login",
    component: LoginPage,
  },
  {
    path: "/",
    component: AppLayout,
    children: [
      {
        path: "",
        name: "home",
        component: () => import("./components/home/Home.vue"),
      },
      {
        path: "dashboard",
        name: "dashboard",
        component: () => import("./components/dashboard/DashboardPage.vue"),
        meta: {
          privilege: "read-dashboard-widgets",
          breadcrumb: [{ label: "Dashboard" }],
        },
      },
      {
        path: "preferences",
        component: () => import("./components/preferences/UserPreferences.vue"),
        children: [
          { path: "", name: "user-preferences", redirect: { name: "preferences-appearance" } },
          {
            path: "appearance",
            name: "preferences-appearance",
            component: () => import("./components/preferences/PreferencesAppearance.vue"),
            meta: { breadcrumb: [{ label: "Preferences", to: "/preferences" }, { label: "Appearance" }] },
          },
          {
            path: "sites",
            name: "preferences-sites",
            component: () => import("./components/preferences/PreferencesSites.vue"),
            meta: { breadcrumb: [{ label: "Preferences", to: "/preferences" }, { label: "Preferred Sites" }] },
          },
          {
            path: "measurements",
            name: "preferences-measurements",
            component: () => import("./components/preferences/PreferencesMeasurements.vue"),
            meta: { breadcrumb: [{ label: "Preferences", to: "/preferences" }, { label: "Units & Formats" }] },
          },
        ],
      },
      {
        path: "assets",
        name: "assets",
        component: () => import("./components/inventory/Inventory.vue"),
        meta: { privilege: "read-equipment", breadcrumb: [{ label: "Assets" }] },
      },
      {
        path: "equipment",
        name: "equipment",
        component: () => import("./components/inventory/InventoryEquipment.vue"),
        meta: {
          privilege: "read-equipment",
          breadcrumb: [{ label: "Equipment" }],
        },
      },
      {
        path: "categories",
        name: "categories",
        component: () => import("./components/categories/Categories.vue"),
        meta: {
          privilege: "read-equipment",
          breadcrumb: [{ label: "Categories" }],
        },
      },
      {
        path: "models",
        name: "models",
        component: () => import("./components/models/Models.vue"),
        meta: {
          privilege: "read-equipment",
          breadcrumb: [{ label: "Models" }],
        },
      },
      {
        path: "reports/asset-hierarchy-checks",
        name: "asset-hierarchy-checks",
        component: () => import("./components/inventory/AssetHierarchyChecks.vue"),
        meta: {
          privilege: "read-equipment",
          breadcrumb: [
            { label: "Reports", to: "/reports" },
            { label: "Asset Hierarchy Checks" },
          ],
        },
      },
      {
        path: "assets/:eliCode",
        name: "asset-details",
        component: () => import("./components/assets/details/AssetDetails.vue"),
        meta: {
          privilege: "read-equipment",
          breadcrumb: (r: { params: { eliCode: string } }) => [
            { label: "Assets", to: "/assets" },
            { label: r.params.eliCode },
          ],
        },
      },
      {
        path: "assets/:eliCode/equipment/:equipmentID",
        name: "equipment-details",
        component: () => import("./components/assets/details/EquipmentDetails.vue"),
        meta: {
          privilege: "read-equipment",
          breadcrumb: (r: { params: { eliCode: string; equipmentID: string } }) => [
            { label: "Assets", to: "/assets" },
            { label: r.params.eliCode, to: `/assets/${encodeURIComponent(r.params.eliCode)}` },
            { label: `Equipment #${r.params.equipmentID}` },
          ],
        },
      },
      {
        path: "work-orders",
        name: "work-orders",
        component: () => import("./components/work-orders/WorkOrders.vue"),
        meta: {
          privilege: "read-workorder",
          breadcrumb: [{ label: "Work Orders" }],
        },
      },
      {
        // Static segment, so it is matched before work-orders/:workOrderID.
        path: "work-orders/fsr",
        name: "fsr-blotter",
        component: () => import("./components/work-orders/fsr/FsrBlotter.vue"),
        meta: {
          privilege: "read-fsr",
          feature: "fsr",
          breadcrumb: [
            { label: "Work Orders", to: "/work-orders" },
            { label: "FSR Blotter" },
          ],
        },
      },
      {
        // Deliberately no privilege meta: the guard takes a single privilege while access here is
        // any-of the report cards' privileges — the page shows a "No reports available" callout
        // instead of bouncing, and each report route below enforces its own privilege.
        path: "reports",
        name: "reports",
        component: () => import("./components/reports/ReportsPage.vue"),
        meta: {
          breadcrumb: [{ label: "Reports" }],
        },
      },
      {
        path: "reports/gas-actions",
        name: "work-order-gas-report",
        component: () => import("./components/work-orders/gas-report/WorkOrderGasReport.vue"),
        meta: {
          privilege: "read-workorder",
          breadcrumb: [
            { label: "Reports", to: "/reports" },
            { label: "Gas Actions Report" },
          ],
        },
      },
      {
        path: "work-orders/:workOrderID",
        name: "work-order-details",
        component: () => import("./components/work-orders/details/WorkOrderDetails.vue"),
        meta: {
          privilege: "read-workorder",
          breadcrumb: (r: { params: { workOrderID: string } }) => [
            { label: "Work Orders", to: "/work-orders" },
            { label: `#${r.params.workOrderID}` },
          ],
        },
      },
      {
        path: "create-work-order",
        name: "create-work-order",
        redirect: { name: "work-orders", query: { action: "create" } },
        meta: { privilege: "write-workorder" },
      },
      {
        path: "pm-schedules",
        name: "pm-schedules",
        component: () => import("./components/pm-schedules/PMSchedules.vue"),
        meta: {
          privilege: "read-pm-template",
          breadcrumb: [{ label: "PM Schedules" }],
        },
      },
      {
        path: "pm-schedules/:scheduledWorkOrderID",
        name: "pm-schedule-details",
        component: () => import("./components/pm-schedules/details/PMScheduleDetails.vue"),
        meta: {
          privilege: "read-pm-template",
          breadcrumb: (r: { params: { scheduledWorkOrderID: string } }) => [
            { label: "PM Schedules", to: "/pm-schedules" },
            { label: `#${r.params.scheduledWorkOrderID}` },
          ],
        },
      },
      {
        path: "tasks",
        name: "tasks",
        component: () => import("./components/tasks/Tasks.vue"),
        meta: { privilege: "read-task", breadcrumb: [{ label: "Tasks" }] },
      },
      {
        path: "tasks/:taskCode",
        name: "task-details",
        component: () => import("./components/tasks/details/TaskDetails.vue"),
        meta: {
          privilege: "read-task",
          breadcrumb: (r: { params: { taskCode: string } }) => [
            { label: "Tasks", to: "/tasks" },
            { label: r.params.taskCode },
          ],
        },
      },
      {
        path: "pm-calendar",
        name: "pm-calendar",
        component: () => import("./components/pm-calendar/PMCalendarPage.vue"),
        meta: {
          privilege: "read-calendar",
          breadcrumb: [{ label: "PM Calendar" }],
        },
      },
      {
        path: "moratoria",
        name: "moratoria",
        component: () => import("./components/moratoria/Moratoria.vue"),
        meta: {
          privilege: "read-moratorium",
          breadcrumb: [{ label: "Moratoria" }],
        },
      },
      {
        path: "moratorium-conflicts",
        name: "moratorium-conflicts",
        component: () => import("./components/moratoria/MoratoriumConflicts.vue"),
        meta: {
          privilege: "read-moratorium",
          breadcrumb: [{ label: "Moratorium Conflicts" }],
        },
      },
      {
        path: "moratoria/:moratoriumID",
        name: "moratorium-details",
        component: () => import("./components/moratoria/details/MoratoriumDetails.vue"),
        meta: {
          privilege: "read-moratorium",
          breadcrumb: (r: { params: { moratoriumID: string } }) => [
            { label: "Moratoria", to: "/moratoria" },
            { label: `#${r.params.moratoriumID}` },
          ],
        },
      },
      {
        path: "create-task",
        name: "create-task",
        redirect: { name: "tasks", query: { action: "create" } },
        meta: { privilege: "write-task" },
      },
      {
        path: "create-moratorium",
        name: "create-moratorium",
        redirect: { name: "moratoria", query: { action: "create" } },
        meta: { privilege: "write-moratorium" },
      },
      // Pre-reorg paths (bookmarks/deep links) → new canonical routes.
      { path: "inventory", redirect: "/assets" },
      { path: "inventory/equipment", redirect: "/equipment" },
      { path: "inventory/asset-hierarchy-checks", redirect: "/reports/asset-hierarchy-checks" },
      {
        path: "inventory/assets/:eliCode",
        redirect: (to: RouteLocation) => ({ name: "asset-details", params: to.params }),
      },
      {
        path: "inventory/assets/:eliCode/equipment/:equipmentID",
        redirect: (to: RouteLocation) => ({ name: "equipment-details", params: to.params }),
      },
      { path: "work-orders/gas-actions", redirect: "/reports/gas-actions" },
      { path: "moratoria/conflicts", redirect: "/moratorium-conflicts" },
      {
        path: "admin",
        component: () => import("./components/admin/Admin.vue"),
        meta: { privilege: "sysadmin" },
        children: [
          {
            path: "configuration",
            component: () => import("./components/admin/AdminSettings.vue"),
            meta: { breadcrumb: [{ label: "Admin", to: "/admin" }, { label: "Settings" }] },
            children: [
              {
                path: "",
                name: "admin-configuration",
                redirect: { name: "admin-config-privileges" },
              },
              {
                path: "privileges",
                name: "admin-config-privileges",
                component: () => import("./components/configuration/AdminPrivilege.vue"),
              },
              {
                path: "criticality-levels",
                name: "admin-config-criticality-levels",
                component: () => import("./components/configuration/AdminCriticalityLevel.vue"),
              },
              {
                path: "system",
                name: "admin-config-system",
                component: () => import("./components/configuration/AdminSystemConfig.vue"),
              },
              {
                path: "fsr",
                name: "admin-config-fsr",
                component: () => import("./components/configuration/AdminFsrSettings.vue"),
                meta: { feature: "fsr" },
              },
              {
                path: "substances",
                name: "admin-config-substances",
                component: () => import("./components/configuration/AdminSubstance.vue"),
              },
              // PR-832 renamed Fluid Types to Substances. Keep the old path working
              // for existing bookmarks and links.
              {
                path: "fluid-types",
                redirect: { name: "admin-config-substances" },
              },
              {
                path: "substance-types",
                name: "admin-config-substance-types",
                component: () => import("./components/configuration/AdminSubstanceType.vue"),
              },
              {
                path: "measurement-types",
                name: "admin-config-measurement-types",
                component: () => import("./components/configuration/AdminMeasurementType.vue"),
                meta: { privilege: "som_writer", feature: "measurements" },
              },
              {
                path: "units-of-measure",
                name: "admin-config-units-of-measure",
                component: () => import("./components/configuration/AdminUnitOfMeasure.vue"),
                meta: { privilege: "som_writer" },
              },
              {
                path: "input-formats",
                name: "admin-config-input-formats",
                component: () => import("./components/configuration/AdminInputFormat.vue"),
              },
              {
                path: "unit-of-measure-conversions",
                name: "admin-config-unit-of-measure-conversions",
                component: () => import("./components/configuration/AdminUnitOfMeasureConversion.vue"),
                meta: { privilege: "som_writer", feature: "measurements" },
              },
              {
                path: "measurement-library",
                name: "admin-config-measurement-library",
                component: () => import("./components/configuration/AdminMeasurementLibrary.vue"),
                meta: { privilege: "sysadmin", feature: "measurements" },
              },
              {
                path: "action-types",
                name: "admin-config-action-types",
                component: () => import("./components/configuration/AdminActionType.vue"),
              },
              {
                path: "maintenance-intervals",
                name: "admin-config-maintenance-intervals",
                component: () => import("./components/configuration/AdminMaintenanceInterval.vue"),
              },
              { path: "document-types", name: "admin-config-document-types", component: () => import("./components/configuration/AdminDocType.vue") },
              {
                path: "organizations",
                name: "admin-config-organizations",
                component: () => import("./components/organizations/Organizations.vue"),
              },
              {
                path: "roles",
                name: "admin-roles",
                component: () => import("./components/admin/roles/AdminRoles.vue"),
              },
              {
                path: "roles/:roleId",
                name: "admin-role-details",
                component: () => import("./components/admin/roles/AdminRoleDetails.vue"),
                props: true,
                meta: {
                  breadcrumb: (r: { params: { roleId: string } }) => [
                    { label: "Admin", to: "/admin" },
                    { label: "Settings", to: "/admin/configuration" },
                    { label: "Roles", to: "/admin/configuration/roles" },
                    { label: `#${r.params.roleId}` },
                  ],
                },
              },
              {
                path: "assay-templates",
                name: "admin-config-assay-templates",
                component: () => import("./components/admin/assay-templates/AdminAssayTemplates.vue"),
                meta: { feature: "assays" },
              },
              {
                path: "/admin/users",
                name: "admin-users",
                component: () => import("./components/admin/users/AdminUsers.vue"),
              },
              {
                path: "/admin/users/:userId",
                name: "admin-user-details",
                component: () => import("./components/admin/users/AdminUserDetails.vue"),
                props: true,
                meta: {
                  breadcrumb: (r: { params: { userId: string } }) => [
                    { label: "Admin", to: "/admin" },
                    { label: "Settings", to: "/admin/configuration" },
                    { label: "Users", to: "/admin/users" },
                    { label: `#${r.params.userId}` },
                  ],
                },
              },
              {
                path: "/admin/tenants",
                name: "admin-tenants",
                component: () => import("./components/admin/tenants/AdminTenants.vue"),
              },
              {
                path: "/admin/widgets",
                name: "admin-widgets",
                component: () => import("./components/admin/widgets/AdminDashboards.vue"),
              },
              {
                path: "/admin/widgets/:dashboardId(\\d+)",
                name: "admin-dashboard-details",
                component: () => import("./components/admin/widgets/AdminDashboardDetails.vue"),
                props: true,
                meta: {
                  breadcrumb: (r: { params: { dashboardId: string } }) => [
                    { label: "Admin", to: "/admin" },
                    { label: "Settings", to: "/admin/configuration" },
                    { label: "Library", to: "/admin/widgets" },
                    { label: `#${r.params.dashboardId}` },
                  ],
                },
              },
            ],
          },
          { path: "", name: "admin", redirect: { name: "admin-configuration" } },
        ],
      },
      {
        path: "forbidden",
        name: "forbidden",
        component: () => import("./components/system/Forbidden.vue"),
      },
      {
        path: "not-found",
        name: "not-found",
        component: () => import("./components/system/NotFound.vue"),
      },
      {
        path: "ords/:apex(.*)*",
        name: "ords-deep-link",
        component: () => import("./components/system/OrdsDeepLink.vue"),
      },
      {
        path: ":pathMatch(.*)*",
        redirect: "/",
      },
    ],
  },
];

const router = createRouter({
  history: createWebHistory(import.meta.env.VUE_APP_BASE_URL),
  routes,
});

router.beforeEach(async (to) => {
  // Persist any unsaved UI preferences from the page being left (no-op unless dirty).
  void UserPreferencesService.flush();

  // Block navigation until bootstrap finishes — services and auth state are ready
  if (!bootstrapReady.value) {
    await new Promise<void>((resolve) => {
      const unwatch = watch(bootstrapReady, (ready) => {
        if (ready) {
          unwatch();
          resolve();
        }
      });
    });
  }

  const token = sessionStorage.getItem("sessionToken");
  const expireRaw = sessionStorage.getItem("sessionExpire");
  const sessionExpired = !!expireRaw && Date.now() >= new Date(expireRaw).getTime();

  // Treat an expired session the same as no session — clean up immediately
  if (sessionExpired) {
    sessionStorage.removeItem("sessionToken");
    sessionStorage.removeItem("sessionExpire");
  }

  const isAuthenticated = !!token && !sessionExpired;

  // Authenticated users don't need the login page
  if (to.name === "login" && isAuthenticated) {
    return { path: "/" };
  }

  // Unauthenticated / expired users go to login, preserving intended destination
  if (to.name !== "login" && !isAuthenticated) {
    if (sessionExpired) {
      sessionStorage.setItem("authError", "Your session has timed out. Please sign in again.");
    }
    if (to.path === "/") {
      return { name: "login" };
    }

    return { name: "login", query: { redirect: to.fullPath } };
  }

  // Feature gate (PR-870), before the privilege check. not-found rather than forbidden: forbidden
  // would admit the page exists, which is what a hidden feature must not do.
  if (!isRouteFeatureEnabled(to.meta)) {
    return { name: "not-found" };
  }

  // Privilege check for authenticated users, against the tenant scope the user is working in
  const action = to.meta?.privilege as string | undefined;
  if (action && !canInAnyTenant(action)) {
    return { name: "forbidden" };
  }

  return true;
});

installChunkLoadRecovery(router);

export default router;
